// Today's search snippets, over the issue's markdown in the local store:
// punctuation gone, each query term marked as a word prefix, ignoring case
// and accents. `…` leads a snippet cut to keep its first match in view.

/** An excerpt of markdown as a line of text: marks, markers and line breaks gone. */
export function plainSnippet(text: string): string {
  return text
    .replace(/!\[([^\]\n]*)\]\([^)\n]*\)/g, '$1')
    .replace(/\[([^\]\n]+)\]\([^)\n]*\)/g, '$1')
    .replace(/^[ \t]{0,3}(?:#{1,6}|>|[-*+]|\d+[.)])[ \t]+/gm, '')
    .replace(/(\*\*|__|~~)(?=\S)([^\n]*?\S)\1/g, '$2')
    .replace(/`+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const WORD = /[\p{L}\p{N}]/u
const TERM = /[\p{L}\p{N}]+/gu

/** Lower case, accents dropped: `Énergie` → `energie`. */
function foldText(text: string): string {
  return text.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase()
}

/** The query's terms, folded, as the server splits them. */
export function queryTerms(query: string): string[] {
  return [...new Set((foldText(query).match(TERM) ?? []).filter(Boolean))]
}

export interface SnippetPart {
  text: string
  match: boolean
}

/** `text` cut into parts, the ones where a query term starts a word marked. */
export function highlightTerms(text: string, query: string): SnippetPart[] {
  const terms = queryTerms(query)
  if (!text || terms.length === 0) return text ? [{ text, match: false }] : []

  // Fold code point by code point, remembering where each folded character came from.
  let folded = ''
  const origin: number[] = []
  let at = 0
  for (const ch of text) {
    const f = foldText(ch)
    for (let k = 0; k < f.length; k++) origin.push(at)
    folded += f
    at += ch.length
  }
  origin.push(at)

  const ranges: [number, number][] = []
  for (const term of terms) {
    for (let i = folded.indexOf(term); i >= 0; i = folded.indexOf(term, i + 1)) {
      if (i > 0 && WORD.test(folded[i - 1]!)) continue
      const start = origin[i]!
      const end = Math.max(origin[i + term.length]!, start + 1)
      ranges.push([start, end])
    }
  }
  if (ranges.length === 0) return [{ text, match: false }]

  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1])
  const parts: SnippetPart[] = []
  let cursor = 0
  for (const [start, end] of ranges) {
    if (end <= cursor) continue
    const from = Math.max(start, cursor)
    const last = parts.at(-1)
    if (from > cursor) parts.push({ text: text.slice(cursor, from), match: false })
    else if (last?.match) {
      // Overlapping or touching matches read as one.
      last.text += text.slice(from, end)
      cursor = end
      continue
    }
    parts.push({ text: text.slice(from, end), match: true })
    cursor = end
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), match: false })
  return parts
}

/**
 * Parts that start a little before the first match, so a narrow palette
 * that cuts the line at its end still shows why the result matched.
 */
export function leadToFirstMatch(parts: SnippetPart[], lead = 24): SnippetPart[] {
  const first = parts.findIndex((p) => p.match)
  if (first <= 0) return parts
  const before = parts.slice(0, first).map((p) => p.text).join('')
  if (before.length <= lead) return parts
  // Cut at a word boundary `lead` characters or so before the match.
  let cut = before.length - lead
  const space = before.indexOf(' ', cut)
  if (space >= 0 && space < before.length - 1) cut = space + 1
  const kept = before.slice(cut)
  return [{ text: `…${kept}`, match: false }, ...parts.slice(first)]
}
